/* eslint-disable max-len */
/* eslint-disable @next/next/no-img-element */
import {Button} from 'antd';
import React, {useContext, useEffect, useImperativeHandle, useRef, useState} from 'react';
import useCommon from '@utils/hooks/common';
import dayjs from 'dayjs';
import {PlayerComponent as PlayerComponentInterface} from '@interface';
import classNames from 'classnames';
import {CHIP_TRANSITION_DURATION, PLAYER_LAYOUT} from '@constant/game';
import Poker from '@components/Poker';
import {
  getBestCombinationText,
  getCumulativeOffset,
  getOrdinal,
  getPlayerCurrentBet,
  getPlayerCurrentMoney,
  getRelativePosition,
} from '@utils/index';
import {useDebugView} from '@utils/hooks/debug';
import styles from './styles';
import MovableChip from '../MovableChip';
import PlayerAvatarChipBorder from '@components/PlayerAvatarChipBorder';
import MultiUnitStatistic from '../MultiUnitStatistic';
import {Statistic} from 'antd';
import PokerOutcard from '@components/PokerOutcard';
import {getPokerBack} from '@utils/index';
import {
  EmojiContext,
  PositionContext,
  TabBubbleHandVisibleContext,
  TournamentContext,
} from '@utils/context';
import NewTimer from './NewTimer/canvas';
import EmojiButton from './EmojiButton';
import {AnimatePresence, motion} from 'framer-motion';
import {get} from 'lodash-es';

const BB_DELAY_SECONDS = 0.5;
const DECISION_DURATION = 0.3;

const PlayerComponent = React.forwardRef(
  (playerInfo: PlayerComponentInterface, ref): JSX.Element => {
    const {
      username,
      order,
      currentBet,
      currentDecision,
      icon,
      hand: handOnResultPhase,
      handLength,
      isInAction,
      isActive,
      isSatOut,
      isTournamentSatOut,
      isDisconnected,
      isFolded,
      isAllInMode,
      isAllIn,
      gameMode,
      winPercentage,
      actionTime,
      isResultShowing,
      isTimerHidden,
      disconnectedAt,
      forceKickAt,
      outCards,
      myHands,
      myBestCombination,
      sequenceOrder,
      isCurrentTableInView,
      noOfPlayers,
      tableElement,
      roomCode,
    } = playerInfo;

    const isOmaha = gameMode === 'OMAHA';

    const {classes, t, game, language, socket, system, theme, user} = useCommon({
      withTranslation: ['game_room', 'player_action'],
      withStyles: styles,
      observers: ['socket', 'game', 'system', 'user'],
    });
    const isDebugView = useDebugView();
    const timerRef = useRef<any>();
    const cardBackRef = useRef<HTMLImageElement>();
    const chipRef = useRef<any>();
    const positionRef = useRef<HTMLElement>();
    const playerIconRef = useRef<HTMLDivElement>(null);
    const playerChipPlacementRef = useRef<HTMLDivElement>(null);
    const playerInfoContainerRef = useRef<HTMLDivElement>(null);

    const [chipValue, setChipValue] = useState<number>(0);
    const [isChipMoving, setIsChipMoving] = useState<boolean>(false);
    const [playCardDistributionAnimation, setPlayCardDistributionAnimation] = useState(false);
    const [tablePosition, setTablePosition] = useState({
      left: 0,
      top: 0,
      right: 0,
      bottom: 0,
    });
    const [cardBackPosition, setCardBackPosition] = useState({
      left: 0,
      top: 0,
      right: 0,
      bottom: 0,
    });
    const [showCurrentDecision, setShowCurrentDecision] = useState<boolean>(false);
    const [shouldHideTimer, setShouldHideTimer] = useState(false);
    const [cardZIndex, setCardZIndex] = useState(1);
    const [isDistributingCards, setIsDistributingCards] = useState(false);

    const isDesktop = theme?.currentStyleFormat?.id === 'desktop';
    const {isGameOverlayShown} = system;

    const {isTournament, playerRankings} = React.useContext(TournamentContext);

    const {componentsPositions, scaleUpValue, updatePlayerChipPlacementPosition} =
      useContext(PositionContext);
    const DESKTOP_ICON_WIDTH = 122;

    const {setIsMyHandVisibleInBubble} = useContext(TabBubbleHandVisibleContext);

    const updatePlayerChipCollectionPosition = () => {
      if (!playerChipPlacementRef?.current) return;

      const absolutePosition = getCumulativeOffset(playerChipPlacementRef.current);

      if (absolutePosition.width > 0 && absolutePosition.height > 0) {
        const {top, left, right, bottom, height, width} = absolutePosition;
        const xCenter = left + width / 2;
        const yCenter = top + height / 2;
        updatePlayerChipPlacementPosition(sequenceOrder, {
          top,
          left,
          right,
          bottom,
          xCenter,
          yCenter,
        });
      }
    };

    useEffect(() => {
      setTimeout(() => {
        // Let DOM update before re-calculation
        updatePlayerChipCollectionPosition();
      }, 500);

      let timeoutId: ReturnType<typeof setTimeout> | null = null;

      const handleResizeEnd = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        timeoutId = setTimeout(() => {
          updatePlayerChipCollectionPosition();
        }, 500); // adjust delay as needed
      };

      window.addEventListener('resize', handleResizeEnd);
      return () => {
        window.removeEventListener('resize', handleResizeEnd);
      };
    }, [isDesktop, scaleUpValue, isCurrentTableInView]);

    useImperativeHandle(
      ref,
      () => ({
        moveChipToPot: async () => {
          // console.log(' @@@ moveChipToPot');
          setIsChipMoving(true);
          chipRef.current.moveToPot();
          await setTimeout(
            () =>
              new Promise(resolve => {
                resolve(null);
              }),
            CHIP_TRANSITION_DURATION
          );
          setIsChipMoving(false);
          setChipValue(currentBet);
        },
        // getAbsolutePosition: (): AbsolutePosition => {
        //   if (!positionRef?.current) return null;
        //   return getCumulativeOffset(positionRef.current);
        // },
        // getPlayerIconCenterPosition: () => {
        //   if (!playerIconRef?.current) return null;
        //   const {top, left, width, height} = playerIconRef.current.getBoundingClientRect();
        //   const centerXOfPlayerIcon = left + width / 2;
        //   const centerYOfPlayerIcon = top + height / 2;
        //   return {
        //     x: centerXOfPlayerIcon,
        //     y: centerYOfPlayerIcon,
        //   };
        // },

        playerOrder: order,
        containerElement: playerInfoContainerRef?.current,
        resetTimer: ({expiredAt, duration}: {expiredAt: dayjs.Dayjs; duration: number}) => {
          if (timerRef.current) {
            timerRef.current?.stopTimer();
            timerRef.current?.resetTimer(expiredAt, duration);
            timerRef.current?.startTimer();
          }
        },
        hideTimer: () => {
          if (timerRef.current) {
            timerRef.current?.hideTimer();
            setShouldHideTimer(true);
          }
        },
        unhideTimer: () => {
          if (timerRef.current) {
            timerRef.current?.unhideTimer();
            setShouldHideTimer(false);
          }
        },
        distributeCards: () => {
          console.log(' **** distributeCards - roomCode **** ', roomCode);
          setIsDistributingCards(true);
          setCardZIndex(4);
        },
      }),
      [currentBet, playerInfoContainerRef, order]
      // [currentBet, playerInfoContainerRef, order, totalDelayInMs, handLength]
    );

    const xBetweenTableCenterAndCardBackCenter =
      ((tablePosition.right - tablePosition.left) / 2 -
        cardBackPosition.left -
        (cardBackPosition.right - cardBackPosition.left) / 2) /
      scaleUpValue;

    const yBetweenTableCenterAndCardBackCenter =
      ((tablePosition.bottom - tablePosition.top) * 0.55 -
        // * 0.55 is slightly lower than the center
        cardBackPosition.top -
        (cardBackPosition.bottom - cardBackPosition.top) / 2) /
      scaleUpValue;

    useEffect(() => {
      if (cardBackRef.current && tableElement) {
        const tablePos = tableElement.getBoundingClientRect();

        if (tablePos.top !== tablePosition.top && tablePos.left !== tablePosition.left) {
          setTablePosition({
            left: tablePos.left,
            top: tablePos.top,
            right: tablePos.right,
            bottom: tablePos.bottom,
          });
        }

        const relativePosition = getRelativePosition(cardBackRef.current, tableElement);
        const {top, left, right, bottom} = relativePosition;
        if (cardBackPosition?.top !== top || cardBackPosition?.left !== left) {
          setCardBackPosition({left, top, right, bottom});
        }
        // * below can cause infinite rendering
        // setCardBackPosition({left, top, right, bottom});
      }
    }, [
      cardBackPosition,
      componentsPositions,
      tableElement,
      tablePosition.left,
      tablePosition.top,
    ]);

    // useEffect(() => {
    //   getPotChipsPositions();

    //   let timeoutId: ReturnType<typeof setTimeout> | null = null;

    //   const handleResizeEnd = () => {
    //     if (timeoutId) {
    //       clearTimeout(timeoutId);
    //     }

    //     timeoutId = setTimeout(() => {
    //       getPotChipsPositions();
    //     }, 500); // adjust delay as needed
    //   };

    //   window.addEventListener('resize', handleResizeEnd);
    //   return () => {
    //     window.removeEventListener('resize', handleResizeEnd);
    //   };
    // }, [isDesktop, scaleUpValue, isGameOverlayShown]);

    useEffect(() => {
      if (isChipMoving) {
        setTimeout(() => {
          setChipValue(currentBet);
        }, CHIP_TRANSITION_DURATION);
      } else {
        setChipValue(currentBet);
      }
    }, [currentBet]);

    useEffect(() => {
      if (isTimerHidden && timerRef.current) {
        timerRef.current?.stopTimer();
      }
    }, [isTimerHidden]);

    useEffect(() => {
      setShowCurrentDecision(true);
    }, [currentDecision]);

    useEffect(() => {
      // if (!isResultShowing && handLength !== 0 && !hasAnimationPlayed) {
      if (handLength !== 0) {
        setPlayCardDistributionAnimation(true);
        // setHasAnimationPlayed(true);
      }
      if (isResultShowing) {
        // setHasAnimationPlayed(false);
        setPlayCardDistributionAnimation(false);
        // setCount(0);
      }
    }, [handLength, isResultShowing]);

    const getMyPlayerId = () => get(user, 'playerInfo.id', null);

    const isMyself = () => {
      if (isDebugView) return order === 0;
      return playerInfo?.id === getMyPlayerId();
    };

    const isCurrentDecisionAllIn = () => {
      return isAllIn || false;
    };

    const isCurrentStatusSatOut = () => {
      return isSatOut || isTournamentSatOut || false;
    };

    const emojiContext = useContext(EmojiContext);
    const {toggleStickerDrawerVisibility, isEmojiSending, isEmojiButtonShown} = emojiContext;

    const getIconWidth = (): number => {
      if (isDesktop) return 80;
      if (!isDesktop) {
        if (isMyself()) return 80;
        return 60;
      }
    };

    const playerIconVariants = {
      initial: {left: 0},
      moveToLeft: {
        left: '-50%',
        transform: 'translateX(50%)',
        filter: 'brightness(0.5)',
      },
      moveToRight: {
        left: '50%',
        transform: 'translateX(-50%)',
        filter: 'brightness(0.5)',
      },
      myselfMoveToRight: {x: '25%', filter: 'brightness(0.5)'},
    };

    const getPlayerIconAnimation = () => {
      // 0
      if (playerInfo.layout === PLAYER_LAYOUT.PLAYER_LEFT_ACTION_RIGHT) {
        if (isMyself()) {
          return isFolded ? 'myselfMoveToRight' : 'initial';
        }
        return isFolded ? 'moveToRight' : 'initial';
      }
      // 1
      if (playerInfo.layout === PLAYER_LAYOUT.PLAYER_RIGHT_ACTION_LEFT) {
        return isFolded ? 'moveToLeft' : 'initial';
      }
      // 3
      if (playerInfo.layout === PLAYER_LAYOUT.PLAYER_TOP_ACTION_BOTTOM) {
        return isFolded ? 'moveToRight' : 'initial';
      }

      return 'initial';
    };

    const renderPlayerIcon = (): JSX.Element => (
      <>
        {isPlayerAtBottom() ? renderWinSign() : null}
        <motion.div
          className={classNames(classes.playerIconContainer, {})}
          ref={playerIconRef}
          animate={getPlayerIconAnimation()}
          variants={playerIconVariants}
          transition={{duration: 0.5}}
        >
          <img
            src="/images/game-room/Player/winner-light.png"
            alt="winner-icon"
            className={classNames(classes.winnerLight, {
              hidden: !playerInfo.isWinner,
            })}
          />
          <img
            src="/images/game-room/Player/winner-light.png"
            alt="winner-icon"
            className={classNames(classes.secondaryWinnerLight, {
              hidden: !playerInfo.isWinner,
            })}
          />
          <div
            className={classNames(classes.timerContainer, {
              [classes.hidden]: !isInAction || isDisconnected || isResultShowing,
            })}
          >
            {/* <Timer timeLimit={actionTime * 1000} ref={timerRef} /> */}
          </div>
          <PlayerAvatarChipBorder iconWidthPx={getIconWidth()} icon={icon} />
        </motion.div>
      </>
    );

    const handleEmojiClick = () => {
      emojiContext.toggleStickerDrawerVisibility(true);
      emojiContext.setReceiverId(playerInfo.id);
    };

    const getCardDelay = numberOfPlayers => {
      const delayMap = [0.2, 0.2, 0.2, 0.1, 0.1, 0.1];
      return delayMap[numberOfPlayers - 1] || 0.1;
    };

    const CARD_DELAY = getCardDelay(noOfPlayers);
    const DURATION_FROM_TABLE_TO_PLAYER = 0.5;
    const MY_CARD_FLIP_DURATION_MS = 300;

    const calculateTotalAnimationTimeInMs = () => {
      // const lastCardIndex = myHands.length - 1;
      const lastCardIndex = handLength - 1;

      const lastPlayerIndex = noOfPlayers - 1;

      const totalAnimationDuration =
        DURATION_FROM_TABLE_TO_PLAYER + // duration for the card animation itself
        lastPlayerIndex * CARD_DELAY + // delay for the last player
        lastCardIndex * (CARD_DELAY * noOfPlayers); // stagger delay for each card

      return totalAnimationDuration * 1000; // to ms
    };

    const totalDelayInMs = calculateTotalAnimationTimeInMs();

    useEffect(() => {
      // console.log('🚀 ~ totalDelayInMs in useEffect', totalDelayInMs);
      if (playCardDistributionAnimation) {
        setTimeout(() => {
          setCardZIndex(1);
          setIsDistributingCards(false);
          // handLength * 100 === stagger delay for each hand to flip
        }, totalDelayInMs + handLength * 100 + BB_DELAY_SECONDS * 1000 + MY_CARD_FLIP_DURATION_MS);
      }
    }, [totalDelayInMs, playCardDistributionAnimation, handLength]);

    const getTopPosition = () => {
      // for err handling
      if (cardBackPosition.top === 0) return 0;
      if (isResultShowing) return 0;
      return yBetweenTableCenterAndCardBackCenter;
    };

    const getLeftPosition = () => {
      if (cardBackPosition.left === 0) return 0;
      if (isResultShowing) return 0;
      return xBetweenTableCenterAndCardBackCenter;
    };

    const cardVariants = {
      cardsAtCenter: {
        opacity: isResultShowing ? 1 : 0,
        top: getTopPosition(),
        left: getLeftPosition(),
      },
      cardsAtPlayer: {
        opacity: 1,
        top: 0,
        left: 0,
      },
      // exit: {opacity: 0},
    };

    const getCardTransition = (cardIndex: number) => {
      return {
        opacity: {
          duration: DURATION_FROM_TABLE_TO_PLAYER,
          delay:
            order * CARD_DELAY +
            cardIndex * (CARD_DELAY * noOfPlayers) +
            BB_DELAY_SECONDS +
            DECISION_DURATION,
        },
        top: {
          duration: DURATION_FROM_TABLE_TO_PLAYER,
          delay:
            order * CARD_DELAY +
            cardIndex * (CARD_DELAY * noOfPlayers) +
            BB_DELAY_SECONDS +
            DECISION_DURATION,
        },
        left: {
          duration: DURATION_FROM_TABLE_TO_PLAYER,
          delay:
            order * CARD_DELAY +
            cardIndex * (CARD_DELAY * noOfPlayers) +
            BB_DELAY_SECONDS +
            DECISION_DURATION,
        },
      };
    };

    const displayMyCards = (): JSX.Element => (
      <motion.div
        exit={{opacity: 0}}
        animate={{
          zIndex: cardZIndex,
          scale: cardZIndex === 4 ? 1.1 : 1,
        }}
        transition={{
          scale: {duration: 0.5},
          ease: 'easeOut',
        }}
        className={classNames(myHands?.length > 0 ? classes.myCardsContainer : undefined, {
          [classes.enlargedCardAnimation]: !isOmaha && playerInfo.isWinner && isMyself(),
          [classes.omahaCardList]: gameMode === 'OMAHA',
          [classes.isFolded]: isFolded && isMyself(),
          [classes.hidden]: handOnResultPhase !== null,
        })}
      >
        {myHands?.map((currentPlayerHand, index) => {
          return (
            <motion.div
              key={`${currentPlayerHand.suit}_${currentPlayerHand.rank}_${index}`}
              style={{position: 'relative'}}
              initial="cardsAtCenter"
              onAnimationComplete={def => {
                console.log('animation complete', def);
                if (index === 0 && isCurrentTableInView) {
                  if (def === 'cardsAtPlayer') {
                    // Add delay for the cards to flip
                    setTimeout(() => {
                      setIsMyHandVisibleInBubble(true);
                    }, 300);
                  } else if (def === 'cardsAtCenter') {
                    setTimeout(() => {
                      setIsMyHandVisibleInBubble(false);
                    }, 300);
                  }
                }
              }}
              variants={cardVariants}
              animate={playCardDistributionAnimation ? 'cardsAtPlayer' : 'cardsAtCenter'}
              transition={getCardTransition(index)}
            >
              <Poker
                className={classNames(classes[`myCard_${index}`], {
                  omaha: gameMode === 'OMAHA',
                  [classes.enlargedCardAnimation]: !isOmaha && playerInfo.isWinner && isMyself(),
                  // [classes.enlargedCardAnimationOmaha]:
                  //   isOmaha && playerInfo.isWinner && isMyself(),
                })}
                // totalDelayInMs === the total time taken to distribute all cards
                // (index + 1) * 100 === stagger delay for each hand to flip
                // BB_DELAY_SECONDS * 1000 === delayed time of BB's chips => coz we want to flip the cards after the chips are placed & cards are distributed
                // TODO @@ maybe need to add chips duration too
                flipDelayFromParent={totalDelayInMs + (index + 1) * 100 + BB_DELAY_SECONDS * 1000}
                state={currentPlayerHand.state}
                suit={currentPlayerHand.suit}
                rank={currentPlayerHand.rank}
                cardSize={55}
              />
            </motion.div>
          );
        })}
      </motion.div>
    );

    if (!isMyself() && roomCode === '') {
      // console.log(' ** shouldHideTimer', shouldHideTimer);
      // console.log(' @@ username, layout', username, playerInfo.layout as number);
    }

    // TODO maybe handLength is the issue

    const displayCardsBack = (): JSX.Element => {
      return (
        <>
          {/* // * no need card back animation if isMyself but still need the info for myHands animation */}
          <motion.div
            ref={cardBackRef}
            style={{opacity: isMyself() ? 0 : 1}}
            animate={{
              zIndex: cardZIndex,
              scale: cardZIndex === 4 ? 1.1 : 1,
            }}
            transition={{
              scale: {duration: 0.5},
              ease: 'easeOut',
            }}
            className={classNames(classes.cardsBackContainer, {
              playersRHS: playerInfo.layout === PLAYER_LAYOUT.PLAYER_RIGHT_ACTION_LEFT,
              myCardsBackContainer: isMyself(),
              omaha: gameMode === 'OMAHA',
            })}
          >
            {Array.from({length: handLength}).map((_, index) => (
              <motion.img
                key={`cardBack_${index}`}
                src={getPokerBack()}
                alt=""
                className={classNames(classes[`cardBack_${index}`], {
                  omaha: gameMode === 'OMAHA',
                  [classes.foldedCards]: isFolded,
                })}
                initial="cardsAtCenter"
                variants={cardVariants}
                animate={playCardDistributionAnimation ? 'cardsAtPlayer' : 'cardsAtCenter'}
                transition={getCardTransition(index)}
              />
            ))}
          </motion.div>
        </>
      );
    };

    const renderHandOnResultPhase = (): JSX.Element => (
      // otherwise playerMoney will cover the icon if the player fold
      <div
        className={classNames(
          handOnResultPhase?.length > 0 ? classes.playerHandForResultPhase : undefined,
          {
            [classes.myPlayerHandForResultPhase]: isMyself(),
            [classes.omahaResultCardList]: gameMode === 'OMAHA',
            [classes.playerHandForResultPhaseRHS]:
              playerInfo.layout === PLAYER_LAYOUT.PLAYER_RIGHT_ACTION_LEFT,
          }
        )}
      >
        {handOnResultPhase?.map((currentPlayerHand, index) => (
          // {[
          //   {
          //     suit: 'DIAMOND',
          //     rank: 7,
          //   },
          //   {
          //     suit: 'DIAMOND',
          //     rank: 3,
          //   },
          // ]?.map((currentPlayerHand, index) => (
          <Poker
            className={classNames(classes[`cardOnResultPhase_${index}`], {
              omaha: gameMode === 'OMAHA',
              [classes.otherPlayersCardsOnWinningPhase]: true,
              [classes.myCardOnWinningPhase]: isMyself(),
            })}
            // className={classes[`myCard_${index}`]}
            key={currentPlayerHand.suit + currentPlayerHand.rank + index}
            state={currentPlayerHand.state}
            suit={currentPlayerHand.suit}
            rank={currentPlayerHand.rank}
            cardSize={45}
          />
        ))}
      </div>
    );

    const getStatusText = (): string => {
      if (isDisconnected) {
        return t('player_status.disconnected');
      }
      if (isSatOut || isTournamentSatOut) {
        return t('player_status.sat_out');
      }
      if (currentDecision?.length > 1) {
        if (isAllIn) {
          return t('player_action:all_in');
        } else {
          return t(`player_action:${currentDecision?.toLocaleLowerCase()}`);
        }
      }
      if (!playerInfo?.isResultShowing && !winPercentage) {
        if (
          handLength > 0 &&
          getPlayerCurrentMoney(playerInfo) === 0 &&
          getPlayerCurrentBet(playerInfo) === 0
        ) {
          return t('player_action:all_in');
        }
      }

      return '';
    };

    const renderDisconnectedTimer = (
      disconnectedAtLocal: any,
      forceKickAtLocal: any
    ): JSX.Element => {
      if (disconnectedAtLocal && forceKickAtLocal) {
        return (
          <Statistic.Countdown
            value={forceKickAtLocal}
            format="mm:ss"
            valueStyle={{
              color: 'white',
              lineHeight: '105%',
              fontSize: '13px',
              fontWeight: 'bold',
              textAlign: 'center',
            }}
          />
        );
      }
      return null;
    };

    const renderEmptyBox = (): JSX.Element => {
      return <div className={classes.hiddenButOccupySpace} />;
    };
    const getTransformStyle = () => {
      if (isDesktop && isAllInMode) {
        return `translate(-${DESKTOP_ICON_WIDTH}px, -35px)`;
      }
      if (isDesktop) {
        return `translate(-${DESKTOP_ICON_WIDTH}px, -20px)`;
      }
      return null;
    };

    const renderBestCombination = (): JSX.Element => {
      const leftPosition =
        typeof window === 'undefined' || isDesktop
          ? null
          : (window.innerWidth * scaleUpValue - window.innerWidth) * 0.5;
      // exceedPart out of screen = (window.innerWidth * scaleUpValue - window.innerWidth);

      return (
        <div
          className={classNames(classes.combinationContainer, {
            [classes.hiddenButOccupySpace]: !myBestCombination?.combinationName,
            [classes.myBestCombinationALLin]: isAllInMode,
            [classes.myBestCombination]: !isAllInMode,
          })}
          style={{
            left: leftPosition,
            transform: getTransformStyle(),
          }}
        >
          {isDesktop ? (
            <>
              <div className={classes.bestCombinationTriangleDesktop} />
              <div className={classes.bestCombinationBodyDesktop}>
                <div
                  className={classNames(classes.combinationText, classes.gradientText, {
                    [classes.greyGradient]: myBestCombination?.combinationName === 'HI_CARD',
                    [classes.blueGradient]:
                      myBestCombination?.combinationName === 'ONE_PAIR' ||
                      myBestCombination?.combinationName === 'PAIR' ||
                      myBestCombination?.combinationName === 'TWO_PAIRS' ||
                      myBestCombination?.combinationName === 'THREE_OF_A_KIND',
                    [classes.goldGradientWithShadow]:
                      myBestCombination?.combinationName === 'ROYAL_FLUSH',
                  })}
                >
                  {getBestCombinationText(t, myBestCombination)}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className={classes.bestCombinationBody}>
                <div
                  className={classNames(classes.combinationText, classes.gradientText, {
                    [classes.greyGradient]: myBestCombination?.combinationName === 'HI_CARD',
                    [classes.blueGradient]:
                      myBestCombination?.combinationName === 'ONE_PAIR' ||
                      myBestCombination?.combinationName === 'PAIR' ||
                      myBestCombination?.combinationName === 'TWO_PAIRS' ||
                      myBestCombination?.combinationName === 'THREE_OF_A_KIND',
                    [classes.goldGradientWithShadow]:
                      myBestCombination?.combinationName === 'ROYAL_FLUSH',
                  })}
                >
                  {getBestCombinationText(t, myBestCombination)}
                </div>
              </div>
              <div className={classes.bestCombinationTriangle} />
            </>
          )}
        </div>
      );
    };

    const shouldRenderDecision = (): boolean =>
      showCurrentDecision && currentDecision && currentDecision !== 'FOLD';

    const renderPlayerInfoSection = (): JSX.Element => {
      return (
        <div
          onClick={() => {
            if (isDebugView && timerRef.current) {
              timerRef.current?.stopTimer();
              timerRef.current?.resetTimer();
              timerRef.current?.startTimer();
            }
          }}
          className={classNames(classes.playerInfoContainer, {
            // isInActive: !isActive || (isFolded && !isMyself()),
            isInActive: !isActive,
          })}
          ref={playerInfoContainerRef}
        >
          {!isPlayerAtBottom() ? renderWinSign() : null}
          {/* <Button
            size="small"
            style={{
              position: 'absolute',
              top: '0%',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 999,
              background: 'grey',
            }}
            onClick={() => {
              setAreCardsMovedToPlayer(!areCardsMovedToPlayer);
            }}
          >
            click {order}
          </Button> */}
          <AnimatePresence>
            {shouldRenderDecision() && (
              <motion.div
                key={currentDecision}
                initial={{opacity: 0}}
                animate={{opacity: 1}}
                exit={{opacity: 0}}
                transition={{
                  duration: DECISION_DURATION,
                  delay: currentDecision === 'BB' ? BB_DELAY_SECONDS : 0,
                }}
                className={classNames(classes.currentDecision, {
                  [classes.myDecision]: isMyself(),
                  [classes.pinkBox]: currentDecision === 'ALL_IN',
                  [classes.orangeBox]: currentDecision === 'RAISE',
                  [classes.yellowBox]: currentDecision === 'BET',
                  [classes.greenBox]: currentDecision === 'CALL',
                  [classes.blueBox]:
                    currentDecision === 'CHECK' ||
                    currentDecision === 'SB' ||
                    currentDecision === 'BB',
                  leftOffset: isMyself() && myBestCombination?.combinationName && !isDesktop,
                })}
              >
                {getStatusText()}
              </motion.div>
            )}
          </AnimatePresence>

          {/* persistently display fold */}
          <AnimatePresence>
            {isFolded && (
              <motion.div
                className={classNames(classes.persistentFoldLabel, {
                  isMyself: isMyself(),
                  leftOffset: isMyself() && myBestCombination?.combinationName && !isDesktop,
                })}
                initial={{opacity: 0}}
                animate={{opacity: 1}}
                exit={{opacity: 0}}
                transition={{duration: 0.3}}
              >
                {t('player_action:fold')}
              </motion.div>
            )}
          </AnimatePresence>
          <div
            className={classNames({
              [classes.playerRHS]: playerInfo.layout === PLAYER_LAYOUT.PLAYER_RIGHT_ACTION_LEFT,
            })}
          >
            {renderPlayerIcon()}
            {handOnResultPhase === null ? displayCardsBack() : renderHandOnResultPhase()}
            {/* // * AnimatePresence is to fade out myCards  */}
            <AnimatePresence>{handLength !== 0 && isMyself() && displayMyCards()}</AnimatePresence>
          </div>
          <div
            className={classNames(classes.playerInfoBox, {
              isMyself: isMyself(),
              [classes.isFolded]: isFolded,
            })}
          >
            <EmojiButton isDisabled={isEmojiSending} onClick={handleEmojiClick} size={25} />
            <div
              className={classNames({
                [classes.hiddenTimer]:
                  !isInAction ||
                  isDisconnected ||
                  isResultShowing ||
                  isTimerHidden ||
                  shouldHideTimer ||
                  isDistributingCards,
              })}
            >
              <NewTimer timeLimit={actionTime * 1000} ref={timerRef} />
            </div>
            <div className={classes.playerName}>{username}</div>
            <div className={classes.whiteDivider} />
            {isDisconnected ? (
              renderDisconnectedTimer(disconnectedAt, forceKickAt)
            ) : (
              <div className={classes.playerBalance}>
                {isCurrentDecisionAllIn() ? (
                  <span className={classes.currentDecisionAllin}>{t('player_action:all_in')}</span>
                ) : isCurrentStatusSatOut() ? (
                  <span className={classes.currentDecisionAllin}>{t('player_status.sat_out')}</span>
                ) : (
                  <MultiUnitStatistic
                    valueStyle={{
                      color: 'white',
                      lineHeight: '15px',
                      fontSize: isDesktop ? 10 : 12,
                    }}
                    value={getPlayerCurrentMoney(playerInfo)}
                  />
                )}
              </div>
            )}
          </div>
          {isMyself() && renderEmptyBox()}
          {/* NOTE: need renderEmptyBox, or else will affect player component layout */}
        </div>
      );
    };

    const isPlayerAtBottom = (): boolean => {
      const {layout} = playerInfo;
      if (
        layout === PLAYER_LAYOUT.ALL_IN_BOTTOM_ACTION_TOP ||
        layout === PLAYER_LAYOUT.PLAYER_BOTTOM_ACTION_TOP
      ) {
        return true;
      }
      return false;
    };

    const renderWinSign = () => {
      const {layout} = playerInfo;
      if (playerInfo.isWinner) {
        return (
          <img
            src="/images/game-room/Player/win.png"
            alt="winner-icon"
            className={classNames(classes.winnerTextImage, {
              playerLeftActionRight:
                layout === PLAYER_LAYOUT.PLAYER_LEFT_ACTION_RIGHT ||
                layout === PLAYER_LAYOUT.PLAYER_TOP_ACTION_BOTTOM,
              playerRightActionLeft: layout === PLAYER_LAYOUT.PLAYER_RIGHT_ACTION_LEFT,
              isMyself: isMyself(),
            })}
          />
        );
      }
      return null;
    };

    const renderOutCards = (): Array<JSX.Element> =>
      outCards?.map(({suit, rank, state}, index) => (
        <PokerOutcard
          key={suit + rank + index}
          state={state}
          suit={suit}
          rank={rank}
          cardSize="extraSmall"
        />
      ));

    const renderWinnerPercentage = () => {
      if (!playerInfo) return null;
      const {layout} = playerInfo;
      let winPercentageContainer: string = '';
      let advantagous: boolean;
      if (layout === PLAYER_LAYOUT.PLAYER_BOTTOM_ACTION_TOP && isResultShowing === false) {
        winPercentageContainer = classes.topWinPercentage;
      } else if (layout === PLAYER_LAYOUT.PLAYER_BOTTOM_ACTION_TOP && isResultShowing === true) {
        winPercentageContainer = classes.topWinPercentageAndResultIsShowing;
      } else if (layout === PLAYER_LAYOUT.PLAYER_LEFT_ACTION_RIGHT) {
        winPercentageContainer = classes.rightWinPercentage;
      } else if (layout === PLAYER_LAYOUT.PLAYER_RIGHT_ACTION_LEFT) {
        winPercentageContainer = classes.leftWinPercentage;
      } else if (!isDesktop && layout === PLAYER_LAYOUT.PLAYER_TOP_ACTION_BOTTOM) {
        winPercentageContainer = classes.bottomWinPercentage;
      } else if (isDesktop && layout === PLAYER_LAYOUT.PLAYER_TOP_ACTION_BOTTOM) {
        winPercentageContainer = classes.bottomWinPercentage;
      }

      if (winPercentage) {
        advantagous = winPercentage > 50;
      }

      return winPercentage !== null ? (
        <div className={winPercentageContainer}>
          {advantagous ? (
            <div className={classes.greenLabel}>
              {t('win_percentage')} {winPercentage?.toString()}%
            </div>
          ) : (
            <div className={classes.blueLabel}>
              {t('win_percentage')} {winPercentage?.toString()}%
              <section className={classes.outcardsOnTable}>{renderOutCards()}</section>
            </div>
          )}
        </div>
      ) : null;
    };

    const renderTournamentRanking = () => {
      const playerRanking = playerRankings?.find(p => p.playerId === playerInfo.id);
      if (!playerRanking?.rank) return null;

      return (
        <>
          <span style={{fontSize: 18}}>{playerRanking.rank}</span>
          <span style={{fontSize: 12}}>{getOrdinal(playerRanking.rank)}</span>
        </>
      );
    };

    const renderComponent = (): JSX.Element => {
      if (!playerInfo) return null;
      const {layout} = playerInfo;
      let className: string = '';
      let initialX = '0px';
      let initialY = '0px';

      let rankingY = '0px';

      switch (layout) {
        case PLAYER_LAYOUT.PLAYER_BOTTOM_ACTION_TOP:
          className = classes.playerBottomActionTop;
          initialY = '100px'; // moves from the bottom
          break;
        case PLAYER_LAYOUT.PLAYER_LEFT_ACTION_RIGHT:
          className = classes.playerLeftActionRight;
          initialX = '-100px';
          rankingY = '50px';
          break;
        case PLAYER_LAYOUT.PLAYER_LEFT_ACTION_RIGHT_TOP:
          className = classes.playerLeftActionRight;
          initialX = '-50px';
          initialY = '85px';
          rankingY = '70px';
          break;
        case PLAYER_LAYOUT.PLAYER_RIGHT_ACTION_LEFT:
          className = classes.playerRightActionLeft;
          initialX = '100px';
          rankingY = '50px';
          break;
        case PLAYER_LAYOUT.PLAYER_TOP_ACTION_BOTTOM:
          className = classes.playerTopActionBottom;
          initialY = '-100px'; // moves from the top
          rankingY = '-10px';
          break;
        case PLAYER_LAYOUT.ALL_IN_TOP_LEFT:
          className = classes.playerTopLeftAllIn;
          break;
        case PLAYER_LAYOUT.ALL_IN_TOP_RIGHT:
          className = classes.playerTopRightAllIn;
          break;
        case PLAYER_LAYOUT.ALL_IN_TOP_ACTION_BOTTOM:
          className = classes.playerTopCenterAllIn;
          break;
        case PLAYER_LAYOUT.ALL_IN_BOTTOM_ACTION_TOP:
          className = classes.playerBottomActionTopAllIn;
          break;
        default:
          break;
      }

      return (
        <>
          <article
            className={classNames(className, {
              [classes.isTransparent]:
                !isActive || isDisconnected || isSatOut || isTournamentSatOut,
            })}
            ref={positionRef}
          >
            <div
              style={{
                background: isDebugView ? 'rgba(30, 30, 30, 0.5)' : 'transparent',
                ...(layout === PLAYER_LAYOUT.PLAYER_LEFT_ACTION_RIGHT_TOP && {
                  transform: 'translate(-30px, -85px)',
                }),
              }}
              ref={playerChipPlacementRef}
              className={classes.roleAndChipContainer}
            >
              {/* <AnimatePresence mode={'wait'}> */}
              <motion.div
                key={chipValue}
                initial={{
                  x: initialX,
                  y: initialY,
                }}
                // exit={{opacity: 0}}
                animate={{x: 0, y: 0}} // animate to the original position
                transition={{
                  type: 'spring',
                  stiffness: 185, // increase stiffness for sharper movement
                  damping: 15, // decrease damping for more bounce
                  mass: 1, // can add mass for a more pronounced effect
                  delay: currentDecision === 'BB' ? BB_DELAY_SECONDS : 0,
                }}
              >
                <MovableChip isAllIn={isAllInMode} chipAmount={chipValue} ref={chipRef} />
              </motion.div>
              {/* </AnimatePresence> */}
            </div>
            {renderPlayerInfoSection()}
            {isMyself() && renderBestCombination()}
          </article>

          {isTournament && (
            <div
              className={classes.tournamentRankText}
              style={{
                top: 0,
                left: rankingY,
              }}
            >
              {renderTournamentRanking()}
            </div>
          )}
          {renderWinnerPercentage()}
        </>
      );
    };

    return renderComponent();
  }
);

PlayerComponent.displayName = 'PlayerComponent';

export default React.memo(PlayerComponent);
